import { useState } from 'react';
import { Card, CardHeader, Button, Input, Select, Modal, LoadingSkeleton, Badge, Pagination } from '@nexora/design-system';
import { useToast } from '@nexora/design-system';
import { Eye, Filter, X } from 'lucide-react';
import {
  useAuditLogs,
  useAuditLogDetail,
  useAuditStats,
  type AuditLogFilters,
} from './useAudit';

function formatDateTime(dateStr: string): string {
  if (!dateStr) return '-';
  return new Date(dateStr).toLocaleString('pt-BR');
}

function truncateText(text: string, max: number): string {
  if (!text) return '-';
  return text.length > max ? `${text.slice(0, max)}...` : text;
}

/* A acao e uma string pontuada: `sales.order.create`, `iam.session.login`.
   O verbo e o ultimo segmento — e o que define a cor. Mapear a acao inteira,
   como antes, nunca casava com nada. */
const VERB_VARIANT: Record<string, 'info' | 'warning' | 'success' | 'error' | 'neutral'> = {
  create: 'success',
  update: 'info',
  delete: 'error',
  cancel: 'error',
  reverse: 'error',
  login: 'neutral',
  logout: 'neutral',
  export: 'warning',
  import: 'warning',
  read: 'info',
  view: 'info',
  confirm: 'success',
  approve: 'success',
};

function actionVerb(action: string): string {
  const parts = action.split('.');
  return (parts[parts.length - 1] ?? action).toLowerCase();
}

function actionVariantOf(action: string): 'info' | 'warning' | 'success' | 'error' | 'neutral' {
  return VERB_VARIANT[actionVerb(action)] ?? 'neutral';
}

const ACTOR_TYPE_LABEL: Record<string, string> = {
  USER: 'Pessoa',
  APPLICATION: 'Aplicação',
  JOB: 'Rotina',
  SYSTEM: 'Sistema',
};

const RESULT_LABEL: Record<string, string> = {
  ALLOWED: 'Permitido',
  DENIED: 'Negado',
  FAILED: 'Falhou',
};

const RESULT_VARIANT: Record<string, 'success' | 'warning' | 'error' | 'neutral'> = {
  ALLOWED: 'success',
  DENIED: 'warning',
  FAILED: 'error',
};

/** Iniciais do ator, para o avatar da referência. */
function initials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  const primeira = parts[0]?.[0] ?? '';
  const ultima = parts.length > 1 ? (parts[parts.length - 1]?.[0] ?? '') : '';
  return (primeira + ultima).toUpperCase();
}

/**
 * Campos que o evento alterou.
 *
 * A referência tem colunas "Old Value" e "New Value". O backend guarda `diff`
 * como **string JSON sem forma definida** — nenhum produtor fora do módulo de
 * auditoria a escreve, e não há contrato do que vai dentro. Separar em antes e
 * depois seria inventar uma estrutura. O que dá para dizer com honestidade é
 * QUAIS campos mudaram; o conteúdo inteiro fica no detalhe.
 */
function changedFields(diff: string | null | undefined): string[] {
  if (!diff) return [];
  try {
    const parsed: unknown = JSON.parse(diff);
    if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
      return Object.keys(parsed as Record<string, unknown>);
    }
  } catch {
    // diff que nao e JSON: nao ha campo a nomear
  }
  return [];
}

/* ── Detail Modal ── */

function AuditDetailModal({
  isOpen,
  onClose,
  logId,
}: {
  isOpen: boolean;
  onClose: () => void;
  logId: string | null;
}): JSX.Element {
  const { data, isLoading, error } = useAuditLogDetail(logId ?? '');

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Detalhes do registro de auditoria"
      size="lg"
      footer={
        <Button variant="outline" onClick={onClose}>
          Fechar
        </Button>
      }
    >
      {isLoading && (
        <div style={{ padding: '2rem', textAlign: 'center' }}>
          <LoadingSkeleton />
          <p style={{ fontSize: '0.875rem', color: '#43474e', marginTop: '0.5rem' }}>
            Carregando detalhes...
          </p>
        </div>
      )}

      {error && !isLoading && (
        <div style={{ padding: '2rem', textAlign: 'center', color: '#ba1a1a' }}>
          <p style={{ fontWeight: 600, margin: '0 0 0.5rem' }}>Erro ao carregar detalhes</p>
          <p style={{ fontSize: '0.875rem', margin: 0, color: '#74777f' }}>
            {error instanceof Error ? error.message : 'Erro desconhecido'}
          </p>
        </div>
      )}

      {data && !isLoading && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem' }}>
            <div>
              <span style={{ fontSize: '0.75rem', fontWeight: 600, color: '#43474e', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                Data/Hora
              </span>
              <p style={{ margin: '0.25rem 0 0', fontSize: '0.875rem', color: '#191c1e' }}>
                {formatDateTime(data.createdAt)}
              </p>
            </div>
            <div>
              <span style={{ fontSize: '0.75rem', fontWeight: 600, color: '#43474e', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                Ator
              </span>
              <p style={{ margin: '0.25rem 0 0', fontSize: '0.875rem', color: '#191c1e' }}>
                {data.actorName || data.actorId}
                {data.actorType ? ` (${data.actorType})` : ''}
              </p>
            </div>
            <div>
              <span style={{ fontSize: '0.75rem', fontWeight: 600, color: '#43474e', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                Ação
              </span>
              <p style={{ margin: '0.25rem 0 0', fontSize: '0.875rem', color: '#191c1e' }}>
                <Badge variant={actionVariantOf(data.action)}>{data.action}</Badge>
              </p>
            </div>
            <div>
              <span style={{ fontSize: '0.75rem', fontWeight: 600, color: '#43474e', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                Recurso
              </span>
              <p style={{ margin: '0.25rem 0 0', fontSize: '0.875rem', color: '#191c1e' }}>
                {data.resource} #{data.resourceId}
              </p>
            </div>
            <div>
              <span style={{ fontSize: '0.75rem', fontWeight: 600, color: '#43474e', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                IP
              </span>
              <p style={{ margin: '0.25rem 0 0', fontSize: '0.875rem', color: '#191c1e', fontFamily: 'monospace' }}>
                {data.ipAddress}
              </p>
            </div>
            <div>
              <span style={{ fontSize: '0.75rem', fontWeight: 600, color: '#43474e', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                User Agent
              </span>
              <p style={{ margin: '0.25rem 0 0', fontSize: '0.8125rem', color: '#43474e', wordBreak: 'break-word' }}>
                {data.userAgent || '-'}
              </p>
            </div>
          </div>

          {/* Nao ha "descricao" do evento: ha o recurso afetado e o resultado. */}
          <div>
            <span style={{ fontSize: '0.75rem', fontWeight: 600, color: '#43474e', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
              Recurso
            </span>
            <p style={{ margin: '0.25rem 0 0', fontSize: '0.875rem', color: '#191c1e' }}>
              {data.resource}
              {data.resourceId ? ` · ${data.resourceId}` : ''}
              {data.result ? ` · ${data.result}` : ''}
            </p>
          </div>

          {/* `diff` e o que mudou — o campo que a trilha de auditoria guarda.
              Vem como texto JSON; formatado aqui quando for JSON valido. */}
          {data.diff && (
            <div>
              <span style={{ fontSize: '0.75rem', fontWeight: 600, color: '#43474e', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                Alteração
              </span>
              <pre
                style={{
                  margin: '0.5rem 0 0',
                  padding: '0.75rem',
                  background: '#f2f4f6',
                  borderRadius: '6px',
                  fontSize: '0.8125rem',
                  lineHeight: 1.5,
                  overflow: 'auto',
                  maxHeight: '300px',
                  whiteSpace: 'pre-wrap',
                  wordBreak: 'break-word',
                  color: '#191c1e',
                  border: '1px solid #e0e3e5',
                }}
              >
                {(() => {
                  try {
                    return JSON.stringify(JSON.parse(data.diff ?? ''), null, 2);
                  } catch {
                    return data.diff;
                  }
                })()}
              </pre>
            </div>
          )}
        </div>
      )}
    </Modal>
  );
}

/* ── Page ── */

export function AuditPage(): JSX.Element {
  const { addToast } = useToast();

  const [page, setPage] = useState(1);
  const [searchUser, setSearchUser] = useState('');
  const [actionFilter, setActionFilter] = useState('ALL');
  const [actorTypeFilter, setActorTypeFilter] = useState('ALL');
  const [resultFilter, setResultFilter] = useState('ALL');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [detailModalOpen, setDetailModalOpen] = useState(false);
  const [selectedLogId, setSelectedLogId] = useState<string | null>(null);
  const pageSize = 20;

  const filters: AuditLogFilters = {
    /* `actorId`, nao `userId`: o parametro inventado nao chegava ao servidor e
       o campo de busca nao filtrava nada. */
    ...(searchUser.trim() ? { actorId: searchUser.trim() } : {}),
    ...(actionFilter !== 'ALL' ? { action: actionFilter } : {}),
    ...(actorTypeFilter !== 'ALL' ? { actorType: actorTypeFilter } : {}),
    ...(resultFilter !== 'ALL' ? { result: resultFilter } : {}),
    ...(dateFrom ? { dateFrom } : {}),
    ...(dateTo ? { dateTo } : {}),
    page,
    pageSize,
  };

  const { data, isLoading, error } = useAuditLogs(filters);

  /* As acoes do select saem de `/audit/stats`: sao as que ocorreram de fato,
     com a contagem. Lista fixa de verbos nunca casava com `sales.order.create`. */
  const stats = useAuditStats({
    ...(dateFrom ? { dateFrom } : {}),
    ...(dateTo ? { dateTo } : {}),
  });
  const acoesConhecidas = stats.data?.byAction ?? [];

  const logs = data?.data ?? [];
  const total = data?.total ?? 0;
  const totalPages = data?.totalPages ?? Math.ceil(total / pageSize);

  const openDetail = (logId: string): void => {
    setSelectedLogId(logId);
    setDetailModalOpen(true);
  };

  const closeDetail = (): void => {
    setDetailModalOpen(false);
    setSelectedLogId(null);
  };

  const applyFilters = (): void => {
    setPage(1);
    addToast('Filtros aplicados', 'success');
  };

  const clearFilters = (): void => {
    setSearchUser('');
    setActionFilter('ALL');
    setActorTypeFilter('ALL');
    setResultFilter('ALL');
    setDateFrom('');
    setDateTo('');
    setPage(1);
    addToast('Filtros limpos', 'success');
  };

  const hasActiveFilters =
    searchUser.trim() !== '' ||
    actionFilter !== 'ALL' ||
    actorTypeFilter !== 'ALL' ||
    resultFilter !== 'ALL' ||
    dateFrom !== '' ||
    dateTo !== '';

  /* ── Loading State ── */
  if (isLoading && logs.length === 0) {
    return (
      <div className="audit-page">
        <div className="flex flex-col md:flex-row justify-between items-start md:items-end gap-md mb-lg">
          <div>
            <h1 className="font-display-lg text-display-lg text-on-surface m-0">Auditoria</h1>
            <p className="font-body-md text-body-md text-on-surface-variant mt-xs">Trilha de auditoria das operações.</p>
          </div>
        </div>
        <Card>
          <div className="skeleton-table">
            <LoadingSkeleton />
          </div>
        </Card>
      </div>
    );
  }

  /* ── Error State ── */
  if (error && logs.length === 0) {
    return (
      <div className="audit-page">
        <div className="flex flex-col md:flex-row justify-between items-start md:items-end gap-md mb-lg">
          <div>
            <h1 className="font-display-lg text-display-lg text-on-surface m-0">Auditoria</h1>
            <p className="font-body-md text-body-md text-on-surface-variant mt-xs">Trilha de auditoria das operações.</p>
          </div>
        </div>
        <Card>
          <div className="error-state" role="alert">
            <p className="error-title">Erro ao carregar registros de auditoria</p>
            <p className="error-message">{(error as Error).message}</p>
            <Button variant="primary" onClick={() => window.location.reload()}>
              Tentar novamente
            </Button>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div className="audit-page">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-end gap-md mb-lg">
        <div>
          <h1 className="font-display-lg text-display-lg text-on-surface m-0">Auditoria e Governança</h1>
          <p className="font-body-md text-body-md text-on-surface-variant mt-xs">
            Registro completo de quem mudou o que, quando e com que resultado.
          </p>
        </div>
      </div>

      {/* Filters */}
      <Card className="filter-card">
        <div className="filters">
          <div className="filter-group">
            <Input
              label=""
              placeholder="Filtrar por ator (id ou nome)…"
              value={searchUser}
              onChange={(e) => {
                setSearchUser(e.target.value);
                setPage(1);
              }}
            />
          </div>
          <div className="filter-group">
            <Select
              label=""
              value={actionFilter}
              onChange={(e) => {
                setActionFilter(e.target.value);
                setPage(1);
              }}
            >
              <option value="ALL">Todas as ações</option>
              {/* As opções são as ações que de fato ocorreram, com a contagem
                  de cada uma, vindas de `GET /audit/stats`. */}
              {acoesConhecidas.map((a) => (
                <option key={a.action} value={a.action}>
                  {a.action} ({a.count})
                </option>
              ))}
            </Select>
          </div>
          <div className="filter-group">
            <Select
              label=""
              value={actorTypeFilter}
              onChange={(e) => {
                setActorTypeFilter(e.target.value);
                setPage(1);
              }}
            >
              <option value="ALL">Qualquer ator</option>
              <option value="USER">Pessoa</option>
              <option value="APPLICATION">Aplicação</option>
              <option value="JOB">Rotina</option>
              <option value="SYSTEM">Sistema</option>
            </Select>
          </div>
          <div className="filter-group">
            <Select
              label=""
              value={resultFilter}
              onChange={(e) => {
                setResultFilter(e.target.value);
                setPage(1);
              }}
            >
              <option value="ALL">Qualquer resultado</option>
              <option value="ALLOWED">Permitido</option>
              <option value="DENIED">Negado</option>
              <option value="FAILED">Falhou</option>
            </Select>
          </div>
          <div className="filter-group date-group">
            <Input
              label=""
              type="date"
              value={dateFrom}
              onChange={(e) => setDateFrom(e.target.value)}
              placeholder="Data inicio"
            />
          </div>
          <div className="filter-group date-group">
            <Input
              label=""
              type="date"
              value={dateTo}
              onChange={(e) => setDateTo(e.target.value)}
              placeholder="Data fim"
            />
          </div>
          <div className="filter-actions">
            <Button variant="primary" size="sm" onClick={applyFilters}>
              <Filter size={14} style={{ marginRight: '0.375rem' }} />
              Filtrar
            </Button>
            {hasActiveFilters && (
              <Button variant="ghost" size="sm" onClick={clearFilters}>
                <X size={14} style={{ marginRight: '0.375rem' }} />
                Limpar
              </Button>
            )}
          </div>
        </div>
      </Card>

      {/* Results */}
      <Card variant="outlined" padding="0">
        <CardHeader
          className="px-md pt-md"
          action={
            <span className="font-label-md text-label-md text-on-surface-variant tabular-nums">
              {total ?? 0} {(total ?? 0) === 1 ? 'registro' : 'registros'}
            </span>
          }
        >
          <h3 className="font-headline-sm text-headline-sm text-on-surface m-0">Registros de auditoria</h3>
        </CardHeader>
        {/* Loading overlay */}
        {isLoading && (
          <div className="loading-overlay">
            <LoadingSkeleton />
          </div>
        )}

        {/* Empty state */}
        {!isLoading && logs.length === 0 && (
          <div className="empty-state">
            <p>Nenhum registro de auditoria</p>
            {hasActiveFilters && (
              <p className="empty-hint">Tente ajustar os filtros ou limpar a busca.</p>
            )}
          </div>
        )}

        {/* Table */}
        {logs.length > 0 && (
          <div className="table-wrapper">
            <table className="audit-table">
              <thead>
                <tr>
                  <th>Data e hora</th>
                  <th>Ator</th>
                  <th>Ação</th>
                  <th>Recurso</th>
                  <th>Campos alterados</th>
                  <th>Resultado</th>
                  <th>IP</th>
                  <th style={{ textAlign: 'center', width: '5rem' }}>Detalhes</th>
                </tr>
              </thead>
              <tbody>
                {logs.map((log) => {
                  const campos = changedFields(log.diff);
                  const nome = log.actorName || log.actorId || '—';
                  return (
                  <tr key={log.id} className="audit-row">
                    <td className="cell-date">
                      {/* `timestamp` e quando o evento ocorreu; `createdAt` e
                          quando a linha foi gravada. A trilha e sobre o
                          primeiro. */}
                      {formatDateTime(log.timestamp)}
                    </td>
                    <td className="cell-user">
                      <span className="actor">
                        <span
                          className={log.actorType === 'USER' ? 'actor-avatar' : 'actor-avatar actor-avatar--bot'}
                          aria-hidden="true"
                        >
                          {log.actorType === 'USER' ? initials(nome) : '⚙'}
                        </span>
                        <span className="actor-text">
                          <strong>{nome}</strong>
                          <small>{ACTOR_TYPE_LABEL[log.actorType] ?? log.actorType}</small>
                        </span>
                      </span>
                    </td>
                    <td>
                      <Badge variant={actionVariantOf(log.action)}>{log.action}</Badge>
                    </td>
                    <td className="cell-resource">
                      {truncateText(log.resource, 30)}
                      {log.resourceId ? <small className="cell-sub">{log.resourceId.slice(0, 8)}</small> : null}
                    </td>
                    <td className="cell-desc">
                      {campos.length === 0 ? (
                        <span className="cell-muted">—</span>
                      ) : (
                        <span title={campos.join(', ')}>{truncateText(campos.join(', '), 40)}</span>
                      )}
                    </td>
                    <td>
                      <Badge variant={RESULT_VARIANT[log.result] ?? 'neutral'} size="sm">
                        {RESULT_LABEL[log.result] ?? log.result}
                      </Badge>
                    </td>
                    <td className="cell-ip">{log.ipAddress || '-'}</td>
                    <td style={{ textAlign: 'center' }}>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => openDetail(log.id)}
                        aria-label="Ver detalhes"
                      >
                        <Eye size={16} />
                      </Button>
                    </td>
                  </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="pagination-wrapper">
            <Pagination
              page={page}
              totalPages={totalPages}
              onChange={(p) => setPage(p)}
            />
          </div>
        )}
      </Card>

      {/* Detail Modal */}
      <AuditDetailModal
        isOpen={detailModalOpen}
        onClose={closeDetail}
        logId={selectedLogId}
      />

      <style>{`
        .audit-page {
          padding: 1.5rem;
        }
        .record-count {
          font-size: 0.875rem;
          color: #43474e;
        }
        .filter-card {
          margin-bottom: 1rem;
          padding: 1rem;
        }
        .filters {
          display: flex;
          align-items: flex-end;
          gap: 0.75rem;
          flex-wrap: wrap;
        }
        .filter-group {
          flex: 1;
          min-width: 180px;
        }
        .date-group {
          flex: 0 0 160px;
        }
        .filter-actions {
          display: flex;
          gap: 0.5rem;
          align-items: flex-end;
          padding-bottom: 0.125rem;
          flex-shrink: 0;
        }
        .table-wrapper {
          overflow-x: auto;
        }
        .audit-table {
          width: 100%;
          border-collapse: collapse;
          font-size: 0.875rem;
        }
        .audit-table th {
          text-align: left;
          padding: 0.75rem 1rem;
          background: #f2f4f6;
          color: #43474e;
          font-weight: 600;
          font-size: 0.75rem;
          text-transform: uppercase;
          letter-spacing: 0.05em;
          border-bottom: 2px solid #e0e3e5;
          white-space: nowrap; position: sticky; top: 0; z-index: 1;}
        .audit-table td {
          padding: 0.75rem 1rem;
          border-bottom: 1px solid #eceef0;
          color: #191c1e;
          white-space: nowrap;
        }
        .audit-row:hover td {
          background: #eceef0;
        }
        .actor { display: inline-flex; align-items: center; gap: 0.5rem; }
        .actor-avatar { display: inline-flex; align-items: center; justify-content: center; width: 26px; height: 26px; border-radius: 50%; background: #d6e3ff; color: #001b3c; font-family: 'JetBrains Mono', ui-monospace, monospace; font-size: 10px; font-weight: 600; flex-shrink: 0; }
        .actor-avatar--bot { background: #e0e3e5; color: #43474e; font-size: 13px; }
        .actor-text { display: flex; flex-direction: column; line-height: 1.2; }
        .actor-text strong { font-size: 0.875rem; font-weight: 600; color: #191c1e; }
        .actor-text small { font-family: 'JetBrains Mono', ui-monospace, monospace; font-size: 10px; letter-spacing: 0.03em; color: #74777f; }
        .cell-sub { display: block; font-family: 'JetBrains Mono', ui-monospace, monospace; font-size: 10px; color: #74777f; }
        .cell-muted { color: #74777f; }

        .cell-date {
          font-family: monospace;
          font-size: 0.8125rem;
          white-space: nowrap;
        }
        .cell-user {
          font-weight: 500;
        }
        .cell-ip {
          font-family: monospace;
          font-size: 0.8125rem;
          color: #43474e;
        }
        .cell-resource {
          max-width: 180px;
          overflow: hidden;
          text-overflow: ellipsis;
        }
        .cell-desc {
          max-width: 240px;
          overflow: hidden;
          text-overflow: ellipsis;
          color: #43474e;
        }
        .loading-overlay {
          padding: 2rem;
          text-align: center;
        }
        .skeleton-table {
          padding: 2rem;
        }
        .error-state {
          padding: 3rem 2rem;
          text-align: center;
        }
        .error-title {
          font-weight: 600;
          margin: 0 0 0.5rem;
          color: #ba1a1a;
        }
        .error-message {
          font-size: 0.875rem;
          margin: 0 0 1rem;
          color: #74777f;
        }
        .empty-state {
          padding: 3rem 2rem;
          text-align: center;
          color: #74777f;
        }
        .empty-state p {
          margin: 0;
          font-size: 0.875rem;
        }
        .empty-hint {
          margin-top: 0.5rem !important;
          font-size: 0.8125rem !important;
          color: #c4c6cf;
        }
        .pagination-wrapper {
          padding: 1rem;
          border-top: 1px solid #eceef0;
          display: flex;
          justify-content: center;
        }
      `}</style>
    </div>
  );
}